import { Inbox, Loader, TriangleAlert } from 'lucide-react'
import type { ReactNode } from 'react'
import { useT } from '@/shared/i18n/translate'
import { cn } from '@/shared/lib/cn'
import { Button } from './button'

interface StateProps {
  className?: string
}

/** Announced politely to assistive technology via role="status". */
export function LoadingState({ label, className }: StateProps & { label?: string }) {
  const t = useT()
  return (
    <div
      role="status"
      aria-live="polite"
      className={cn('flex items-center justify-center gap-2 p-6 text-muted-foreground', className)}
    >
      <Loader aria-hidden className="size-5 animate-spin motion-reduce:animate-none" />
      <span>{label ?? t('states.loading')}</span>
    </div>
  )
}

export function EmptyState({
  title,
  description,
  icon,
  action,
  className,
}: StateProps & { title?: string; description?: string; icon?: ReactNode; action?: ReactNode }) {
  const t = useT()
  return (
    <div
      className={cn('flex flex-col items-center justify-center gap-3 p-8 text-center', className)}
    >
      <span aria-hidden className="text-muted-foreground [&_svg]:size-10">
        {icon ?? <Inbox />}
      </span>
      <p className="text-base font-semibold">{title ?? t('states.empty')}</p>
      {description ? (
        <p className="max-w-prose text-sm text-muted-foreground">{description}</p>
      ) : null}
      {action}
    </div>
  )
}

export function ErrorState({
  title,
  description,
  onRetry,
  retryLabel,
  className,
  children,
}: StateProps & {
  title?: string
  description?: string
  onRetry?: () => void
  retryLabel?: string
  children?: ReactNode
}) {
  const t = useT()
  return (
    <div
      role="alert"
      className={cn('flex flex-col items-center justify-center gap-3 p-8 text-center', className)}
    >
      <TriangleAlert aria-hidden className="size-10 text-destructive" />
      <p className="text-base font-semibold">{title ?? t('errors.unexpectedTitle')}</p>
      <p className="max-w-prose text-sm text-muted-foreground">
        {description ?? t('errors.unexpectedDescription')}
      </p>
      {onRetry ? (
        <Button variant="outline" onClick={onRetry}>
          {retryLabel ?? t('actions.retry')}
        </Button>
      ) : null}
      {children}
    </div>
  )
}
